/**
 * leaderboard.ts — fetches and renders the per-level leaderboard.
 *
 * The level ID is read from the `?level=<id>` query parameter.
 * If no level ID is present the leaderboard section is hidden.
 * If the API is unavailable the status message is updated accordingly.
 */

import { API_BASE } from './api';

interface ScoreEntry {
  rank: number;
  username: string;
  completionTimeMs: number;
  kills: number;
  casualties: number;
}

interface LeaderboardResponse {
  entries: ScoreEntry[];
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

async function loadLeaderboard(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const levelId = params.get('level');

  const section = document.getElementById('leaderboard') as HTMLElement;
  const status = document.getElementById('leaderboard-status') as HTMLElement;
  const table = document.getElementById('leaderboard-table') as HTMLTableElement;
  const tbody = document.getElementById('leaderboard-body') as HTMLTableSectionElement;

  const resolvedId = levelId ?? sessionStorage.getItem('ff_current_level');

  if (!resolvedId) {
    section.hidden = true;
    return;
  }

  try {
    const response = await fetch(`${API_BASE}/v1/levels/${encodeURIComponent(resolvedId)}/scores?limit=10`);

    if (!response.ok) {
      status.textContent = 'No scores yet — be the first to complete this level.';
      return;
    }

    const data: LeaderboardResponse = await response.json();

    if (data.entries.length === 0) {
      status.textContent = 'No scores yet — be the first to complete this level.';
      return;
    }

    status.hidden = true;
    tbody.innerHTML = '';

    for (const entry of data.entries) {
      const row = document.createElement('tr');
      row.innerHTML = `
        <td>${entry.rank}</td>
        <td>${escapeHtml(entry.username)}</td>
        <td>${formatTime(entry.completionTimeMs)}</td>
        <td>${entry.kills}</td>
        <td>${entry.casualties}</td>
      `;
      tbody.appendChild(row);
    }

    table.hidden = false;
  } catch {
    status.textContent = 'Scores unavailable.';
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

loadLeaderboard();
